import { reactive } from 'vue'
import { useTitle } from '@vueuse/core'
import type { Router } from 'vue-router'
import { i18n } from './index'
import { routeI18nKeys } from './routeI18n'

/**
 * Reactive head state for components that want to render the active
 * route's translated title, description, or breadcrumb without reaching
 * into Vue Router meta.
 */
export const routeHead = reactive({
  title: '',
  description: '',
  breadcrumb: ''
})

const documentTitle = useTitle()

/**
 * Installs a navigation guard that auto-populates each matched route's
 * `meta` (title/description/breadcrumb) from i18n, using the route's
 * `name` as the lookup key in `routeI18nKeys`. The leaf route's title
 * additionally flows into `document.title` and the `routeHead` state.
 *
 * Iterating every matched record is what makes multi-level breadcrumbs
 * work: `BackendTemplate` builds the trail from `route.matched`, filtering
 * by `meta.title` — so parent routes must also have a title set, which
 * means they need a `name` and a mapping entry.
 */
export function installI18nRouterGuard(router: Router): void {
  router.beforeEach((to, _from, next) => {
    const t = (key: string) => i18n.global.t(key) as string

    for (const record of to.matched) {
      const name = typeof record.name === 'string' ? record.name : null
      const prefix = name ? routeI18nKeys[name] : undefined
      if (!prefix) continue

      record.meta.title = t(`${prefix}.title`)
      record.meta.description = t(`${prefix}.description`)
      record.meta.breadcrumb = t(`${prefix}.breadcrumb`)
    }

    const leaf = to.matched[to.matched.length - 1]
    const leafName = typeof leaf?.name === 'string' ? leaf.name : null
    const leafPrefix = leafName ? routeI18nKeys[leafName] : undefined

    if (leafPrefix) {
      routeHead.title = t(`${leafPrefix}.title`)
      routeHead.description = t(`${leafPrefix}.description`)
      routeHead.breadcrumb = t(`${leafPrefix}.breadcrumb`)
      documentTitle.value = routeHead.title
    }

    next()
  })
}
